import React, { forwardRef, ForwardedRef, Ref, useRef } from 'react';
import clsx from 'clsx';
import { useFocusRing } from '@react-aria/focus';
import { AriaSelectProps, useSelect } from '@react-aria/select';
import { useFocusableRef } from '@react-spectrum/utils';
import { useSelectState } from '@react-stately/select';
import styles from '@danske/sapphire-css/components/select/select.module.css';

import { SelectBase } from './SelectBase';
import { SelectProps } from './SelectProps';
import { Node } from '@react-types/shared';
import { HiddenSelect } from './HiddenSelect';

export interface SelectComponentProps<
  T extends object,
  M extends 'single' | 'multiple'
> extends SelectProps<T, M> {
  /**
   * Function to customize the rendered text in the field for a given selection.
   * Note that if there is only one item seleted, that one is rendered in the field.
   * @default (selectedItems) => `${selectedItems.length} selected`
   */
  renderSelectionText?: (selectedItems: Node<T>[]) => string;
}

/**
 * Allows user to select a single option from a list, similar to native select
 * component.
 *
 * Example:
 * ```tsx
 * <Select label="Label text" placeholder="Select an option">
 *  <SelectItem>Option 1</SelectItem>
 *  <SelectItem>Option 2</SelectItem>
 *  <SelectItem>Option 3</SelectItem>
 *  <SelectItem>Option 4</SelectItem>
 * </Select>
 * ```
 */
function _Select<T extends object, M extends 'single' | 'multiple' = 'single'>(
  props: SelectComponentProps<T, M>,
  ref: ForwardedRef<HTMLDivElement>
) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useFocusableRef<HTMLDivElement>(null);
  const listBoxRef = useFocusableRef<HTMLUListElement>(null);

  const {
    renderSelectionText = (selection) => `${selection.length} selected`,
  } = props;

  // TODO v6 - remove casting
  // onSelectionChange type is changed in @react-aria/types@3.29.1 and allows the key parameter
  // to be null. At least our select components don't allow that. If we do call onSelectionChange
  // with null in some case, we should fix the type in a next major version.
  const state = useSelectState(
    props as Omit<typeof props, 'onSelectionChange'> &
      Pick<AriaSelectProps<T>, 'onSelectionChange'>
  );
  const selectAria = useSelect(
    {
      ...(props as Omit<typeof props, 'onSelectionChange'> &
        Pick<AriaSelectProps<T>, 'onSelectionChange'>),
      errorMessage: props.error,
      description: props.note,
      isInvalid: props.error ? true : undefined,
    },
    state,
    triggerRef
  );
  const focusRingAria = useFocusRing();

  return (
    <>
      <SelectBase
        containerRef={ref}
        popoverRef={popoverRef}
        listBoxRef={listBoxRef}
        triggerRef={triggerRef}
        selectProps={props}
        state={state}
        selectAria={selectAria}
        focusRingAria={focusRingAria}
        selectValueContent={
          state.selectedItems.length === 0 ? (
            <div className={clsx(styles['sapphire-select__placeholder'])}>
              {props.placeholder}
            </div>
          ) : state.selectedItems.length === 1 ? (
            <>{state.selectedItems[0].textValue}</>
          ) : (
            <>{renderSelectionText(state.selectedItems)}</>
          )
        }
        size={props.size ?? 'lg'}
        labelPlacement={props.labelPlacement ?? 'above'}
      />
      <HiddenSelect
        {...props}
        isDisabled={props.isDisabled}
        state={state}
        triggerRef={triggerRef}
        label={props.label}
      />
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore This has been an issue while building in different environments https://danskebank.ghe.com/main/ciwc-sapphire/actions/runs/49816582/job/211204737
export const Select = forwardRef(_Select) as <
  T extends object,
  M extends 'single' | 'multiple' = 'single'
>(
  props: SelectComponentProps<T, M> & { ref?: Ref<HTMLDivElement> }
) => React.ReactElement;
